// src/pages/ContactFormPreview.jsx
import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextAreaField from '../components/ui/TextAreaField';
import FormField from '../components/ui/FormField';
import ContactHeader from '../components/ui/ContactHeader';
import { useUser } from '../contexts/UserContext';
import {
  UserIcon,
  SendIcon,
  PhoneIcon,
  MailIcon,
  BookmarkIcon,
  MessageIcon,
} from '../components/common/IconsComponent';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

// ─── Constants ────────────────────────────────────────────────────────────────

const SUBJECT_OPTIONS = [
  { value: 'general', label: 'פנייה כללית' },
  { value: 'support', label: 'תמיכה טכנית' },
  { value: 'billing', label: 'פנייה בנושא תשלום' },
  { value: 'partnership', label: 'שיתופי פעולה' },
];

const VALIDATION_SCHEMA = Yup.object({
  firstName: Yup.string().required('שם פרטי הוא שדה חובה'),
  lastName: Yup.string().required('שם משפחה הוא שדה חובה'),
  email: Yup.string()
    .email('נא להזין כתובת אימייל תקינה')
    .required('אימייל הוא שדה חובה'),
  phone: Yup.string().matches(
    /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
    'נא להזין מספר טלפון תקין'
  ),
  subject: Yup.string().required('נושא הוא שדה חובה'),
  message: Yup.string()
    .min(10, 'ההודעה קצרה מדי (לפחות 10 תווים)')
    .required('הודעה היא שדה חובה'),
});

// ─── Error message ─────────────────────────────────────────────────────────────

const FieldError = ({ message }) =>
  message ? (
    <p className="mt-1.5 flex items-center gap-1 text-xs text-red-500">
      <svg
        className="w-3.5 h-3.5 flex-shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {message}
    </p>
  ) : null;

// ─── SelectField ──────────────────────────────────────────────────────────────

const SelectField = ({ label, name, formik, showError }) => {
  const [focused, setFocused] = useState(false);
  const error = showError ? formik.errors[name] : '';

  return (
    <div>
      <label
        htmlFor={name}
        className={`block text-sm font-medium mb-1.5 transition-colors duration-150 ${
          focused ? 'text-indigo-600' : 'text-gray-700'
        }`}
      >
        {label} <span className="text-red-500">*</span>
      </label>

      <div className="relative">
        <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
          <BookmarkIcon />
        </span>

        <select
          id={name}
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={`
            w-full pr-10 pl-4 py-2.5 text-sm border rounded-xl
            appearance-none bg-white outline-none
            transition-all duration-150 cursor-pointer
            ${
              error
                ? 'border-red-400 bg-red-50 ring-2 ring-red-200'
                : focused
                ? 'border-indigo-400 ring-2 ring-indigo-100 shadow-sm shadow-indigo-50'
                : 'border-gray-200 hover:border-gray-300'
            }
          `}
        >
          {SUBJECT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <FieldError message={error} />
    </div>
  );
};

// ─── SuccessState ─────────────────────────────────────────────────────────────

const SuccessState = () => (
  <div className="flex flex-col items-center justify-center text-center py-12 gap-5">
    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center shadow-sm">
      <svg
        className="w-8 h-8 text-emerald-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3"
        />
      </svg>
    </div>

    <div className="space-y-1">
      <h3 className="text-base font-bold text-gray-900">ההודעה נשלחה בהצלחה!</h3>
      <p className="text-sm text-gray-500">נחזור אליך בהקדם האפשרי.</p>
    </div>
  </div>
);

// ─── Card Header (desktop / tablet) ──────────────────────────────────────────

const CardHeader = () => (
  <div className="hidden sm:flex items-center gap-3 mb-5 pb-4 border-b-2 border-gray-400">
    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-sm">
      <svg
        viewBox="0 0 24 24"
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden="true"
      >
        <path d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    </div>

    <div className="min-w-0">
      <h2 className="text-base font-bold text-gray-800 leading-tight">צור קשר</h2>
      <p className="text-xs text-gray-400 mt-0.5">מלא את הפרטים ונחזור אליך בהקדם</p>
    </div>
  </div>
);

// ─── ContactForm ──────────────────────────────────────────────────────────────

const ContactForm = ({ formik, isSending }) => {
  const showErrors = formik.submitCount > 0;

  return (
    <form onSubmit={formik.handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label="שם פרטי"
          name="firstName"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={undefined}
          placeholder="הזן שם פרטי"
          icon={<UserIcon />}
          required
          error={showErrors ? formik.errors.firstName : ''}
        />
        <FormField
          label="שם משפחה"
          name="lastName"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={undefined}
          placeholder="הזן שם משפחה"
          icon={<UserIcon />}
          required
          error={showErrors ? formik.errors.lastName : ''}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label="אימייל"
          name="email"
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={undefined}
          placeholder="example@domain.com"
          icon={<MailIcon />}
          required
          error={showErrors ? formik.errors.email : ''}
        />
        <FormField
          label="טלפון"
          name="phone"
          type="tel"
          value={formik.values.phone}
          onChange={formik.handleChange}
          onBlur={undefined}
          placeholder="הזן מספר טלפון"
          icon={<PhoneIcon />}
          error={showErrors ? formik.errors.phone : ''}
        />
      </div>

      <SelectField
        label="נושא"
        name="subject"
        formik={formik}
        showError={showErrors}
      />

      <TextAreaField
        label="הודעה"
        name="message"
        value={formik.values.message}
        onChange={formik.handleChange}
        onBlur={undefined}
        placeholder="כתוב כאן את ההודעה שלך..."
        icon={<MessageIcon size={16} />}
        maxLength={300}
        minLength={15}
        required
        error={showErrors ? formik.errors.message : ''}
        dir="rtl"
        characterCountText="תווים"
        showLiveHint={false}
      />

      <div className="flex justify-end md:justify-center pt-1">
        <button
          type="submit"
          disabled={formik.isSubmitting || isSending}
          className="
            inline-flex items-center gap-2
            bg-gradient-to-r from-indigo-600 to-purple-600
            text-white text-sm font-semibold
            px-5 py-2.5 rounded-xl shadow-sm
            hover:opacity-90 active:scale-[0.98]
            transition-all duration-150
            disabled:opacity-50 disabled:cursor-not-allowed
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2
          "
        >
          שלח הודעה
          <SendIcon />
        </button>
      </div>
    </form>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ContactFormPreview = () => {
  const [submitted, setSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { sendContactMessage, showAlert } = useUser();

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      subject: 'general',
      message: '',
    },
    validationSchema: VALIDATION_SCHEMA,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      setIsSending(true);
      try {
        const data = await sendContactMessage(values);

        if (!data?.ok) {
          showAlert?.('שליחת ההודעה נכשלה');
          return;
        }

        setSubmitted(true);
        setTimeout(() => {
          formik.resetForm();
          setSubmitted(false);
        }, 5000);
      } catch (err) {
        showAlert?.(err.message || 'שליחת ההודעה נכשלה');
      } finally {
        setIsSending(false);
      }
    },
  });

  return (
    <div dir="rtl" className="min-h-screen bg-gray-100">
      {isSending && (
        <SpinnerOverlay variant="glow" text="שולח..." dark={false} blur={false} />
      )}

      <div className="sm:hidden sticky top-0 z-10">
        <ContactHeader
          title="צור קשר"
          subtitle="מלא את הפרטים ונחזור אליך בהקדם"
        />
      </div>

      <main className="mx-auto max-w-2xl px-2 pt-2 pb-8 lg:py-10">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
          <div className="h-1 bg-gradient-to-l from-indigo-500 via-purple-500 to-blue-500" />

          <div className="p-4 sm:p-6">
            <CardHeader />

            {submitted ? (
              <SuccessState />
            ) : (
              <ContactForm formik={formik} isSending={isSending} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactFormPreview;